import React, { useState, useContext, useMemo } from "react";
import {
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Snackbar,
  Alert,
  Autocomplete,
  Fab,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import searchIcon from "../../assets/img/icons/search.png";
import axios from "axios";
import { OrderContext } from "../../contexts/OrderContext";

const CreateDesign = () => {
  const { storeData, fetchStoreData } = useContext(OrderContext);
  const { itemtypes = [], designs = [] } = storeData;

  const [showForm, setShowForm] = useState(false);
  const [design, setDesign] = useState({ name: "", itemtype_id: null });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [filterItemType, setFilterItemType] = useState(null);

  const filteredDesigns = useMemo(() => {
    return designs.filter((design) => {
      const matchesSearch = design.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesType = filterItemType
        ? design.itemtype_id === filterItemType.id
        : true;
      return matchesSearch && matchesType;
    });
  }, [searchTerm, designs, filterItemType]);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const mfr_id = localStorage.getItem("id");

  const handleDesignNameChange = (e) => {
    setDesign({ ...design, name: e.target.value });
  };

  const handleItemTypeChange = (_, newValue) => {
    setDesign({ ...design, itemtype_id: newValue ? newValue.id : null });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const requestData = {
        name: design.name,
        itemtype_id: design.itemtype_id,
        mfr_id,
      };

      await axios.post(
        `${API_BASE_URL}/api/fetchorder/CreateDesign`,
        requestData
      );
      fetchStoreData();
      setSnackbar({
        open: true,
        message: "Design created successfully!",
        severity: "success",
      });
      setDesign({ name: "", itemtype_id: null });
      setShowForm(false);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Error creating design",
        severity: "error",
      });
    }

    setLoading(false);
  };

  return (
    <Box p={3} position="relative">
      {!showForm && (
        <Box maxWidth={600} mx="auto">
          <Box
            width="100%"
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            gap="0.4rem"
            sx={{ p: 1, mb: 3 }}
          >
            {/* Filter by Item Type */}
            <Grid item xs={12}>
              <Autocomplete
                fullWidth
                options={itemtypes}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter by Item Type"
                    variant="outlined"
                    sx={{
                      "& label": { color: "#64748B" },
                      "& label.Mui-focused": { color: "#0D7377" },
                      "& .MuiInputBase-input": { color: "#333333" },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#A0ABBB" },
                        "&:hover fieldset": { borderColor: "#A0ABBB" },
                        "&.Mui-focused fieldset": { borderColor: "#0D7377" },
                      },
                    }}
                  />
                )}
                value={filterItemType}
                onChange={(_, newValue) => setFilterItemType(newValue)}
              />
            </Grid>

            {/* Search Input */}
            <div className="custom-search-box" style={{ width: "100%" }}>
              <img src={searchIcon} alt="Search" className="search-icon" />
              <input
                type="text"
                placeholder="Search Design"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Create New Design if not found */}
            {filteredDesigns.length === 0 && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                alignSelf="center"
                sx={{
                  width: "75%",
                  bgcolor: "#0D7377",
                  padding: "0.4rem 0",
                  borderRadius: 2,
                  cursor: "pointer",
                }}
                onClick={() => {
                  setDesign({
                    name: searchTerm,
                    itemtype_id: filterItemType?.id || null,
                  });
                  setShowForm(true);
                }}
              >
                <Typography sx={{ fontSize: 16, color: "#fffffa" }}>
                  Create This Design
                </Typography>
              </Box>
            )}
          </Box>

          {/* Design List */}
          <Grid container spacing={2} mb={10}>
            {filteredDesigns.map((design) => {
              const itemtype = itemtypes.find(
                (it) => it.id === design.itemtype_id
              );
              return (
                <Grid item xs={12} key={design.id}>
                  <Card sx={{ p: 1, boxShadow: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: "14px", fontWeight: "bold" }}
                    >
                      {design.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: "13px", color: "#64748B" }}
                    >
                      {itemtype?.name || "Unknown Item Type"}
                    </Typography>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {/* Form */}
      {showForm && (
        <Card sx={{ maxWidth: 600, mx: "auto" }}>
          <CardContent>
            <Typography
              variant="h5"
              fontWeight="bold"
              textAlign="center"
              mb={2}
              color="#0D7377"
            >
              Create Design
            </Typography>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Design Name"
                    name="name"
                    value={design.name}
                    onChange={handleDesignNameChange}
                    required
                    sx={{
                      "& label": { color: "#64748B" },
                      "& label.Mui-focused": { color: "#0D7377" },
                      "& .MuiInputBase-input": { color: "#333333" },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#A0ABBB" },
                        "&:hover fieldset": { borderColor: "#A0ABBB" },
                        "&.Mui-focused fieldset": { borderColor: "#0D7377" },
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Autocomplete
                    fullWidth
                    options={itemtypes}
                    getOptionLabel={(option) => option.name}
                    value={
                      itemtypes.find(
                        (item) => item.id === design.itemtype_id
                      ) || null
                    }
                    onChange={handleItemTypeChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Item Type"
                        variant="outlined"
                        required
                        sx={{
                          "& label": { color: "#64748B" },
                          "& label.Mui-focused": { color: "#0D7377" },
                          "& .MuiInputBase-input": { color: "#333333" },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": { borderColor: "#A0ABBB" },
                            "&:hover fieldset": { borderColor: "#A0ABBB" },
                            "&.Mui-focused fieldset": {
                              borderColor: "#0D7377",
                            },
                          },
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    disabled={loading || !design.itemtype_id}
                    sx={{
                      backgroundColor: "#0D7377",
                      color: "#fff",
                      "&:hover": { backgroundColor: "#0A5C60" },
                    }}
                  >
                    {loading ? "Saving..." : "Save"}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Floating Button */}
      {showForm && (
        <Fab
          color="primary"
          aria-label="toggle"
          onClick={() => setShowForm(false)}
          sx={{
            position: "fixed",
            bottom: 80,
            right: 24,
            bgcolor: "#0D7377",
            "&:hover": { bgcolor: "#0A5C60" },
          }}
        >
          <CloseIcon />
        </Fab>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        sx={{
          '&.MuiSnackbar-root': {
            bottom: '100px !important', // force override snackbar position
          }
        }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateDesign;
